import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, Download, FileSpreadsheet, ArrowRight, History, ChevronDown } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bt, Cd, Inp } from "./index";
import { notify } from "../notify";
import { fmtAnyDate } from "../dateFormat";

const MODULES = [
  { id: "all", l: "All", c: K.p },
  { id: "leaves", l: "Leaves", c: K.g },
  { id: "attendance", l: "Attendance", c: K.bl },
  { id: "recruitment", l: "Recruitment", c: K.pu },
  { id: "onboarding", l: "Onboarding", c: K.a },
  { id: "employees", l: "Employees", c: K.ro },
  { id: "appraisal", l: "Appraisal", c: K.pu },
  { id: "payroll", l: "Payroll", c: K.tl },
  { id: "expenses", l: "Expenses", c: K.a },
  { id: "assets", l: "Assets", c: K.cy },
  { id: "offboarding", l: "Offboarding", c: K.r },
  { id: "compliance", l: "Compliance", c: K.g },
  { id: "policies", l: "Policies", c: K.p },
  { id: "organization", l: "Organization", c: K.ro },
];
const modColor = (m) => (MODULES.find((x) => x.id === m) || MODULES[0]).c;
const modLabel = (m) => (MODULES.find((x) => x.id === m) || { l: m }).l;
const PAGE = 25;

const pretty = (f) => String(f || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const IST = { timeZone: "Asia/Kolkata" };
const exact = (iso) => new Date(iso).toLocaleString("en-GB", { ...IST, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return exact(iso);
};
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-CA", IST);
const dayLabel = (iso) => {
  const k = dayKey(iso);
  const today = new Date().toLocaleDateString("en-CA", IST);
  const y = new Date(Date.now() - 86400000).toLocaleDateString("en-CA", IST);
  if (k === today) return "Today";
  if (k === y) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-GB", { ...IST, weekday: "long", day: "2-digit", month: "short", year: "numeric" });
};

const Val = ({ v }) => (v == null || v === ""
  ? <span className="text-t3 italic">empty</span>
  : <span className="text-t0 font-semibold break-words">{String(fmtAnyDate(v))}</span>);


// Search box with a type-ahead list drawn from the log itself. ↑/↓ move, Enter
// picks, Esc closes; clicking a suggestion works too.
const SuggestInput = ({ field, module, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [hi, setHi] = useState(-1);
  const box = useRef(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const mine = ++tries.current;
    const t = setTimeout(() => {
      api.get(`/api/activity/suggest?field=${field}&module=${module}&q=${encodeURIComponent(value.trim())}`)
        .then((r) => { if (mine === tries.current && Array.isArray(r)) { setItems(r); setHi(-1); } })
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [open, value, field, module]);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const pick = (it) => { onChange(it.name, true); setOpen(false); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { if (open && hi >= 0 && items[hi]) { e.preventDefault(); pick(items[hi]); } else setOpen(false); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative w-[200px]" ref={box}>
      <Search size={13} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-t3 pointer-events-none" />
      <input value={value} placeholder={placeholder} autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        className="w-full py-[7px] pl-[32px] pr-[26px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none focus:border-p" />
      {value && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange("", true); setOpen(false); }}
        className="absolute right-[8px] top-1/2 -translate-y-1/2 text-t3 hover:text-t0 bg-transparent border-0 cursor-pointer p-0 leading-none text-[16px]" aria-label="Clear">×</button>}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-s border border-b0 rounded-xl shadow-lg max-h-[280px] overflow-y-auto py-1" style={{ minWidth: 240 }}>
          {items.length === 0 && <div className="px-3 py-2 text-[12.5px] text-t3">{value.trim() ? "No matches in the log" : "Start typing…"}</div>}
          {items.map((it, i) => (
            <div key={it.name} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it)} onMouseEnter={() => setHi(i)}
              className={`px-3 py-[7px] cursor-pointer flex items-center gap-2 text-[13px] ${hi === i ? "bg-pg" : ""}`}>
              <span className="font-semibold text-t0 truncate">{it.name}</span>
              {it.ref && <span className="text-t3 text-[11.5px]">{it.ref}</span>}
              <span className="ml-auto text-[11px] text-t3 bg-bg2 rounded-full px-[7px] py-[1px]">{it.n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Who did what, for whom, and what changed — one module (or all of them).
// `module` is leaves | attendance | recruitment | onboarding | employees | all.
export const ActivityLog = ({ module = "all", title = "Activity Log", showModuleFilter = false, compact = false, bare = false, canDownload = true }) => {
  const [mod, setMod] = useState(module);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [qa, setQa] = useState("");
  const [qt, setQt] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState({});
  const seq = useRef(0);

  useEffect(() => { setMod(module); }, [module]);
  useEffect(() => { const t = setTimeout(() => { setQ(search.trim()); setQa(actor.trim()); setQt(target.trim()); }, 350); return () => clearTimeout(t); }, [search, actor, target]);

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    p.set("module", mod);
    if (q) p.set("search", q);
    if (qa) p.set("actor", qa);
    if (qt) p.set("target", qt);
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [mod, q, qa, qt, from, to]);

  const load = useCallback(async (append) => {
    const mine = ++seq.current;
    append ? setMore(true) : setLoading(true);
    setErr("");
    try {
      const d = await api.get(`/api/activity?${qs({ limit: PAGE, offset: append ? rows.length : 0 })}`);
      if (!d || mine !== seq.current) return; // a newer request superseded this one
      setRows((r) => (append ? [...r, ...d.rows] : d.rows));
      setTotal(d.total);
    } catch (e) { if (mine === seq.current) setErr(e.message || "Could not load activity"); }
    finally { if (mine === seq.current) { setLoading(false); setMore(false); } }
    // eslint-disable-next-line
  }, [qs, rows.length]);

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [mod, q, qa, qt, from, to]);

  const download = async (format) => {
    try { await api.download(`/api/activity/download?${qs({ format })}`, `activity_log.${format}`); }
    catch (e) { notify(e.message || "Download failed", { title: "Error", variant: "error" }); }
  };

  let lastDay = null;
  const Wrap = bare ? "div" : Cd;
  return (
    <Wrap>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-b0">
        <div className="flex items-center gap-[10px]">
          <div className="flex items-center justify-center w-7 h-7 rounded-[9px]" style={{ background: `${K.p}17`, border: `1px solid ${K.p}2a` }}><History size={14} color={K.p} /></div>
          <div>
            <div className="text-[16px] font-extrabold text-t0 tracking-tight">{title}</div>
            <div className="text-[12px] text-t3">Who did what, for whom, and what changed{total ? ` · ${total} entr${total === 1 ? "y" : "ies"}` : ""}</div>
          </div>
        </div>
        {canDownload && <div className="flex items-center gap-2">
          <Bt v="secondary" s="sm" icon={FileSpreadsheet} onClick={() => download("xlsx")}>Excel</Bt>
          <Bt v="ghost" s="sm" icon={Download} onClick={() => download("csv")}>CSV</Bt>
        </div>}
      </div>

      <div className="flex flex-wrap items-center gap-[8px] mb-3">
        <SuggestInput field="actor" module={mod} value={actor} placeholder="Done by (name)…" onChange={(v, now) => { setActor(v); if (now) setQa(v.trim()); }} />
        <SuggestInput field="target" module={mod} value={target} placeholder="Done for (name / ID)…" onChange={(v, now) => { setTarget(v); if (now) setQt(v.trim()); }} />
        <SuggestInput field="action" module={mod} value={search} placeholder="Action or field…" onChange={(v, now) => { setSearch(v); if (now) setQ(v.trim()); }} />
        <label className="flex items-center gap-[6px] text-[12px] text-t3 font-semibold">From
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="px-[8px] py-[6px] bg-bg2 border border-b0 rounded-[10px] text-[13px] text-t0 outline-none" /></label>
        <label className="flex items-center gap-[6px] text-[12px] text-t3 font-semibold">To
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="px-[8px] py-[6px] bg-bg2 border border-b0 rounded-[10px] text-[13px] text-t0 outline-none" /></label>
        {(from || to || search || actor || target) && <button onClick={() => { setFrom(""); setTo(""); setSearch(""); setActor(""); setTarget(""); }} className="text-[12px] font-bold text-p bg-transparent border-0 cursor-pointer">Clear</button>}
        {showModuleFilter && (
          <div className="flex flex-wrap gap-[6px] ml-auto">
            {MODULES.map((m) => (
              <button key={m.id} onClick={() => setMod(m.id)}
                className={`px-3 py-[5px] rounded-full text-[12px] font-bold cursor-pointer border transition-colors ${mod === m.id ? "text-white border-transparent" : "bg-s border-b0 text-t2 hover:text-t0"}`}
                style={mod === m.id ? { background: m.c } : undefined}>{m.l}</button>
            ))}
          </div>
        )}
      </div>

      {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3 font-medium">{err}</div>}
      {loading && <div className="text-center py-8 text-t3 text-[14px]">Loading activity…</div>}
      {!loading && !err && rows.length === 0 && (
        <div className="text-center py-10 text-t3 text-[14px]">
          No activity recorded{q || qa || qt || from || to ? " for these filters" : " yet"}. Changes made from now on will appear here.
        </div>
      )}

      <div className={compact ? "max-h-[560px] overflow-y-auto pr-1" : ""}>
        {rows.map((r) => {
          const dk = dayKey(r.created_at);
          const header = dk !== lastDay ? dayLabel(r.created_at) : null;
          lastDay = dk;
          const ch = Array.isArray(r.changes) ? r.changes : [];
          const isOpen = !!open[r.id];
          const shown = isOpen ? ch : ch.slice(0, 3);
          const det = r.details && !ch.length ? Object.entries(r.details).slice(0, 4) : [];
          return (
            <React.Fragment key={r.id}>
              {header && <div className="text-[11px] font-extrabold uppercase tracking-wider text-t3 mt-3 mb-1">{header}</div>}
              <div className="flex gap-[12px] py-[10px] border-b border-b0/70 last:border-0">
                <div className="mt-[6px] w-[9px] h-[9px] rounded-full flex-shrink-0" style={{ background: modColor(r.module) }} title={modLabel(r.module)} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-[14px] font-bold text-t0">{r.action}</span>
                    {r.entity_label && <span className="text-[13px] text-t2">· {r.entity_label}</span>}
                    {(mod === "all" || showModuleFilter) && <span className="text-[10.5px] font-extrabold px-[7px] py-[1px] rounded-full" style={{ background: `${modColor(r.module)}1a`, color: modColor(r.module) }}>{modLabel(r.module)}</span>}
                  </div>
                  <div className="text-[12.5px] text-t2 mt-[2px] flex flex-wrap items-center gap-x-[6px]">
                    <span>by <b className="text-t0">{r.actor_name || r.actor_username || "System"}</b>{r.actor_role ? <span className="text-t3"> ({r.actor_role.replace("_", " ")})</span> : null}</span>
                    {r.target_name && <><ArrowRight size={11} className="text-t3" /><span>for <b className="text-t0">{r.target_name}</b>{r.target_employee_id ? <span className="text-t3"> ({r.target_employee_id})</span> : null}</span></>}
                    <span className="text-t3" title={exact(r.created_at)}>· {ago(r.created_at)}</span>
                  </div>
                  {shown.length > 0 && (
                    <div className="mt-[6px] rounded-lg bg-bg2 border border-b0 px-3 py-[6px] text-[12.5px] space-y-[3px]">
                      {shown.map((c, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-x-[6px]">
                          <span className="text-t3 font-semibold min-w-[110px]">{pretty(c.field)}</span>
                          <Val v={c.from} /><ArrowRight size={11} className="text-t3" /><Val v={c.to} />
                        </div>
                      ))}
                      {ch.length > 3 && (
                        <button onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))} className="text-[12px] font-bold text-p bg-transparent border-0 cursor-pointer p-0 flex items-center gap-1">
                          {isOpen ? "Show less" : `Show ${ch.length - 3} more change${ch.length - 3 === 1 ? "" : "s"}`}<ChevronDown size={12} style={{ transform: isOpen ? "rotate(180deg)" : undefined }} />
                        </button>
                      )}
                    </div>
                  )}
                  {det.length > 0 && (
                    <div className="mt-[4px] text-[12px] text-t3 flex flex-wrap gap-x-3">
                      {det.map(([k, v]) => <span key={k}>{pretty(k)}: <span className="text-t1 font-semibold">{String(fmtAnyDate(v)).slice(0, 80)}</span></span>)}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {rows.length < total && !loading && (
        <div className="text-center mt-3"><Bt v="secondary" s="sm" onClick={() => load(true)} disabled={more}>{more ? "Loading…" : `Load more (${total - rows.length} left)`}</Bt></div>
      )}
    </Wrap>
  );
};
